"use client"

// components/cookie-consent.tsx
//
// The site-wide cookie banner and preferences dialog, mounted once in
// app/layout.tsx. Restyled to Oliver's Paper file "ComplyRx Legal and
// Consent", page "02 Cookie Consent" (FE-COOKIE-1 Phase 1): banner 1JF-0 and
// 1LZ-0, preferences 1K8-0 and 1MR-0, footer link 1OG-0.
//
// THE CONSENT CONTRACT IS UNCHANGED. Other code reads it and is not edited
// (components/posthog-provider.tsx, hooks/use-analytics.ts,
// lib/analytics/google-ads.ts, components/google-ads-tag.tsx,
// components/onboarding-next/cookie-banner-inset.ts):
// - the localStorage key "cookie-consent"
// - the JSON {essential, analytics, marketing}, with exactly the values each
//   action wrote before: Accept All {true,true,true}, Reject All
//   {true,false,false}, Save {true, analytics, marketing}
// - the window event "cookie-consent-updated", fired after every write
//
// No non-essential tracker loads from here. Both consent-gated trackers mount
// themselves and read the stored consent and the event above: PostHog
// (product analytics, gated on `analytics`) in PostHogProvider, and Google Ads
// conversion tracking (gated on `marketing`) in GoogleAdsTag, which injects
// no script at all until marketing consent is present. GTM, Google Analytics,
// Google Ads and Klaviyo were removed 2026-06-24 for the waitlist launch (they
// loaded before or without consent, PECR); Google Ads returned 2026-08-03,
// load-after-consent. GA4 and GTM stay removed. If a tracker is re-added
// later, gate it the same way.
//
// The round 6 markers (FE-WATCH-ONBOARD-1 Phase 10b) stay: `data-cookie-banner`
// on the banner root, and its height as `--cookie-banner-height` on <html>
// while it shows, removed on hide and unmount.
//
// The look lives in presentational parts (CookieBannerView,
// CookiePreferencesPanel) so the localhost preview (app/(marketing)/cookie-next)
// can draw every state without touching storage. The preview's own styles live
// in its own module there, so none of them ship in this site-wide stylesheet. The state lives in the
// default export and the pure helpers below, which the offline test
// (scripts/test-cookie-consent.ts) exercises.

import { useCallback, useEffect, useId, useRef, useState } from "react"
import type { ElementType, ReactNode, Ref } from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Cookie, X } from "lucide-react"

import styles from "./cookie-consent.module.css"

export interface CookiePreferences {
  essential: boolean
  analytics: boolean
  marketing: boolean
}

export const CONSENT_KEY = "cookie-consent"
export const CONSENT_UPDATED_EVENT = "cookie-consent-updated"
export const OPEN_SETTINGS_EVENT = "open-cookie-settings"

/** Accept All writes exactly this, as before. */
export function acceptAllPreferences(): CookiePreferences {
  return { essential: true, analytics: true, marketing: true }
}

/** Reject All writes exactly this, as before. */
export function rejectAllPreferences(): CookiePreferences {
  return { essential: true, analytics: false, marketing: false }
}

/** Save writes the draft's two switches, essential always true, as before. */
export function savedSelection(draft: CookiePreferences): CookiePreferences {
  return { essential: true, analytics: draft.analytics, marketing: draft.marketing }
}

/**
 * The stored choice, or null for "no choice yet". A missing value, a value
 * that is not JSON, or JSON that is not a plain object all count as no choice,
 * so the banner shows again instead of the old unguarded JSON.parse throwing.
 * Nothing is written until the person chooses.
 */
export function parseConsent(raw: string | null | undefined): CookiePreferences | null {
  if (raw == null || raw === "") return null
  let value: unknown
  try {
    value = JSON.parse(raw)
  } catch {
    return null
  }
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null
  return value as CookiePreferences
}

type ConsentStorage = Pick<Storage, "getItem" | "setItem">

/** The stored choice from `storage`, or null. A storage that throws counts as no choice. */
export function readConsent(storage: Pick<Storage, "getItem">): CookiePreferences | null {
  try {
    return parseConsent(storage.getItem(CONSENT_KEY))
  } catch {
    return null
  }
}

/** window.localStorage, or a stand-in that holds nothing when the browser blocks it. */
function browserStorage(): Pick<Storage, "getItem"> {
  try {
    return window.localStorage
  } catch {
    return { getItem: () => null }
  }
}

/** The dialog's switches on open: the saved choice, or all off when there is none. */
export function draftFrom(saved: CookiePreferences | null): CookiePreferences {
  return {
    essential: true,
    analytics: saved?.analytics === true,
    marketing: saved?.marketing === true,
  }
}

/** Write the choice under the contract key, then fire the contract event. */
export function persistConsent(
  prefs: CookiePreferences,
  env: { storage: ConsentStorage; target: EventTarget } = { storage: window.localStorage, target: window },
): void {
  env.storage.setItem(CONSENT_KEY, JSON.stringify(prefs))
  env.target.dispatchEvent(new Event(CONSENT_UPDATED_EVENT))
}

/** Re-open the preferences dialog from anywhere (the footer's "Cookie settings"). */
export function openCookieSettings(): void {
  if (typeof window === "undefined") return
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT))
}

type MarkerRoot = { style: Pick<CSSStyleDeclaration, "setProperty" | "removeProperty"> }
type MarkerEl = { getBoundingClientRect(): { height: number } }

/**
 * Round 6 marker: write the banner's height to `--cookie-banner-height` on
 * <html>, keep it current through a ResizeObserver where there is one, and
 * return the cleanup that removes it.
 */
export function attachBannerMarker(root: MarkerRoot, el: MarkerEl): () => void {
  const write = () =>
    root.style.setProperty("--cookie-banner-height", `${Math.ceil(el.getBoundingClientRect().height)}px`)
  write()
  const ro =
    typeof ResizeObserver !== "undefined" ? new ResizeObserver(write) : null
  ro?.observe(el as unknown as Element)
  return () => {
    ro?.disconnect()
    root.style.removeProperty("--cookie-banner-height")
  }
}

// ── Glyphs ──────────────────────────────────────────────────────────────────

/** Paper's cookie glyph (the same geometry as lucide's Cookie). */
export function CookieGlyph({ size = 20, strokeWidth = 1.75, className }: { size?: number; strokeWidth?: number; className?: string }) {
  return <Cookie aria-hidden="true" focusable="false" width={size} height={size} strokeWidth={strokeWidth} className={className} />
}

// ── Banner ──────────────────────────────────────────────────────────────────

export interface CookieBannerViewProps {
  onManage?: () => void
  onRejectAll?: () => void
  onAcceptAll?: () => void
  /** The live banner's root, for the round 6 height marker. */
  rootRef?: Ref<HTMLDivElement>
  /** Preview only: omit the round 6 marker, so the inset hook ignores this copy. */
  inline?: boolean
  /** Preview only: the preview's own module positions the banner in the page flow. */
  className?: string
}

export function CookieBannerView({ onManage, onRejectAll, onAcceptAll, rootRef, inline = false, className }: CookieBannerViewProps) {
  const titleId = useId()
  const bodyId = useId()
  return (
    <div
      ref={rootRef}
      {...(inline ? {} : { "data-cookie-banner": "" })}
      role="region"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      className={`${styles.banner} ${className ?? ""}`}
    >
      <div className={styles.bannerInner}>
        <div className={styles.bannerIntro}>
          <span className={styles.iconTile} aria-hidden="true">
            <CookieGlyph />
          </span>
          <div className={styles.bannerCopy}>
            <h2 id={titleId} className={styles.bannerTitle}>
              We use cookies
            </h2>
            <p id={bodyId} className={styles.bannerBody}>
              We use cookies to keep the site running and, with your consent, to understand how the site is used. You
              can manage your preferences or learn more in our{" "}
              <a href="/cookie-policy" className={styles.policyLink}>
                cookie policy
              </a>
              .
            </p>
          </div>
        </div>
        <div className={styles.bannerActions}>
          <button type="button" onClick={onManage} className={`${styles.button} ${styles.outline} ${styles.manage}`}>
            <span className={styles.buttonLabel}>Manage</span>
          </button>
          <div className={styles.actionPair}>
            <button type="button" onClick={onRejectAll} className={`${styles.button} ${styles.solid}`}>
              <span className={styles.buttonLabel}>Reject All</span>
            </button>
            <button type="button" onClick={onAcceptAll} className={`${styles.button} ${styles.solid}`}>
              <span className={styles.buttonLabel}>Accept All</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Preferences ─────────────────────────────────────────────────────────────

type OptionalKey = "analytics" | "marketing"

function ConsentSwitch({
  id,
  checked,
  onToggle,
  labelId,
  descriptionId,
}: {
  id: string
  checked: boolean
  onToggle?: () => void
  labelId: string
  descriptionId: string
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      aria-describedby={descriptionId}
      onClick={onToggle}
      className={styles.switch}
    >
      <span className={styles.switchThumb} aria-hidden="true" />
    </button>
  )
}

function ConsentOption({
  title,
  description,
  checked,
  onToggle,
}: {
  title: string
  description: ReactNode
  checked: boolean
  onToggle?: () => void
}) {
  const base = useId()
  const switchId = `${base}-switch`
  const labelId = `${base}-label`
  const descriptionId = `${base}-description`
  return (
    <div className={styles.option}>
      <div className={styles.optionText}>
        {/* A label for the switch button: clicking the title toggles it too. */}
        <label id={labelId} htmlFor={switchId} className={styles.optionTitle}>
          {title}
        </label>
        <p id={descriptionId} className={styles.optionBody} onClick={onToggle}>
          {description}
        </p>
      </div>
      <ConsentSwitch id={switchId} checked={checked} onToggle={onToggle} labelId={labelId} descriptionId={descriptionId} />
    </div>
  )
}

export interface CookiePreferencesPanelProps {
  draft: CookiePreferences
  onToggle?: (key: OptionalKey) => void
  onRejectAll?: () => void
  onSave?: () => void
  onClose?: () => void
  /** The heading element. The live dialog passes Radix's Title, so it names the dialog. */
  TitleAs?: ElementType
  className?: string
}

export function CookiePreferencesPanel({
  draft,
  onToggle,
  onRejectAll,
  onSave,
  onClose,
  TitleAs = "h2",
  className,
}: CookiePreferencesPanelProps) {
  return (
    <div className={`${styles.panel} ${className ?? ""}`}>
      <div className={styles.panelHeader}>
        <TitleAs className={styles.panelTitle}>Cookie Preferences</TitleAs>
        <button type="button" onClick={onClose} aria-label="Close" className={styles.close}>
          <X aria-hidden="true" focusable="false" width={20} height={20} strokeWidth={1.75} />
        </button>
      </div>

      <section className={styles.essential} aria-label="Essential Cookies & Data">
        <div className={styles.essentialHead}>
          <h3 className={styles.essentialTitle}>Essential Cookies &amp; Data</h3>
          <span className={styles.badge}>Always Active</span>
        </div>
        <p className={styles.essentialBody}>Required for the website to function properly. These cannot be disabled.</p>
        <ul className={styles.bullets}>
          <li>
            <span aria-hidden="true">• </span>Authentication tokens (keeps you signed in)
          </li>
          <li>
            <span aria-hidden="true">• </span>Abuse prevention (IP address, 24h retention)
          </li>
          <li>
            <span aria-hidden="true">• </span>Cookie preferences (remembers your choices)
          </li>
        </ul>
      </section>

      <ConsentOption
        title="Analytics Cookies"
        description="Help us understand how visitors interact with our website by collecting anonymous information."
        checked={draft.analytics}
        onToggle={onToggle ? () => onToggle("analytics") : undefined}
      />
      <hr className={styles.divider} />
      {/* Marketing. Added 2026-08-03 alongside Google Ads conversion tracking:
          `marketing` already existed in the stored preferences and was set by
          Accept All, but had no control here, so a visitor using Manage could
          never grant it deliberately, and it is the flag the ad tag gates on. */}
      <ConsentOption
        title="Marketing Cookies"
        description="Let us measure which adverts lead people to ComplyRx, so we know what is worth running. Off by default, and nothing loads until you turn it on."
        checked={draft.marketing}
        onToggle={onToggle ? () => onToggle("marketing") : undefined}
      />

      <div className={styles.panelFooter}>
        <button type="button" onClick={onRejectAll} className={`${styles.button} ${styles.solid}`}>
          <span className={styles.buttonLabel}>Reject All</span>
        </button>
        <button type="button" onClick={onSave} className={`${styles.button} ${styles.solid}`}>
          <span className={styles.buttonLabel}>Save Preferences</span>
        </button>
      </div>
    </div>
  )
}

// ── State ───────────────────────────────────────────────────────────────────

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [draft, setDraft] = useState<CookiePreferences>(() => draftFrom(null))

  useEffect(() => {
    // No choice yet (or an unreadable one): show the banner. Write nothing.
    if (readConsent(browserStorage()) === null) setShowBanner(true)
  }, [])

  const choose = useCallback((prefs: CookiePreferences) => {
    persistConsent(prefs)
    setShowBanner(false)
    setShowSettings(false)
  }, [])

  const acceptAll = useCallback(() => choose(acceptAllPreferences()), [choose])
  const rejectAll = useCallback(() => choose(rejectAllPreferences()), [choose])
  const saveSelected = useCallback(() => choose(savedSelection(draft)), [choose, draft])

  // Every open starts from the saved choice (or all off), so closing without
  // saving discards the switches' unsaved state. The control that opened it
  // (Manage, or the footer's Cookie settings) gets focus back on close: Radix
  // only returns focus to its own Trigger, and neither opener is one.
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const openSettings = useCallback(() => {
    const active = document.activeElement
    returnFocusRef.current = active instanceof HTMLElement && active !== document.body ? active : null
    setDraft(draftFrom(readConsent(browserStorage())))
    setShowSettings(true)
  }, [])
  const restoreFocus = useCallback((event: Event) => {
    event.preventDefault()
    const target = returnFocusRef.current
    returnFocusRef.current = null
    // After a choice from the banner's dialog the banner (and Manage) are
    // gone, so there is nothing to return to and focus stays with the page.
    if (target && target.isConnected) target.focus()
  }, [])
  const closeSettings = useCallback(() => setShowSettings(false), [])

  // The footer's "Cookie settings" (1MR-0): the dialog alone, pre-filled.
  useEffect(() => {
    window.addEventListener(OPEN_SETTINGS_EVENT, openSettings)
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, openSettings)
  }, [openSettings])

  const toggle = useCallback((key: OptionalKey) => {
    setDraft((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  // Round 6 markers, unchanged in behaviour: written while the banner shows,
  // removed on hide and unmount.
  const bannerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const root = document.documentElement
    const el = bannerRef.current
    if (!showBanner || !el) {
      root.style.removeProperty("--cookie-banner-height")
      return
    }
    return attachBannerMarker(root, el)
  }, [showBanner])

  return (
    <>
      {showBanner ? (
        <CookieBannerView rootRef={bannerRef} onManage={openSettings} onRejectAll={rejectAll} onAcceptAll={acceptAll} />
      ) : null}

      <DialogPrimitive.Root open={showSettings} onOpenChange={(open) => (open ? undefined : closeSettings())}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className={styles.overlay} />
          {/* aria-modal: Radix hides the rest of the page from assistive tech
              but does not set it itself (components-dialog-accessibility-1). */}
          <DialogPrimitive.Content
            className={styles.dialog}
            aria-modal="true"
            aria-describedby={undefined}
            onCloseAutoFocus={restoreFocus}
          >
            <CookiePreferencesPanel
              draft={draft}
              onToggle={toggle}
              onRejectAll={rejectAll}
              onSave={saveSelected}
              onClose={closeSettings}
              TitleAs={DialogPrimitive.Title}
            />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  )
}
